import { memo, useState } from 'react';

import CurrentSupplier from 'types/CurrentSupplier';
import ProductPrice from '@templates/ProductCard/components/Price';
import ProductAvailability from '@components/ProductAvailability';
import Modal from '@components/Modal';
import InputQuantity from '@templates/InputQuantity';
import Typography from '@components/atoms/Typography';
import Button, { Props as ButtonProps } from '@components/atoms/Button';
import AskForAvailability from '@templates/product/AskForAvailability';
import Cart from 'assets/icons/plus-cart.svg';
import getMaxQuantity from 'utils/getMaxQuantity';
import { FormattedMessage } from 'utils/intl';
import createStyleVariables from 'utils/createStyleVariables';

import Price from 'types/Price';
import classes from './AddToCart.module.scss';
import messages from './AddToCart.messages';

type ReleasedProductProps = {
  netPrice: Price;
  oldGrossPrice: Price;
  minValue?: number;
  stock: number;
  inShipping?: boolean;
  unavailable?: boolean;
  currentSupplier?: CurrentSupplier;
  withComplementary?: boolean;
  addToCart: (quantity: number) => void;
};

type Props = {
  variantId: number;
  productName: string;
  grossPrice: Price;
} & (
  | ({
      isPrerelease: true;
    } & Partial<Record<keyof ReleasedProductProps, never>>)
  | ({
      isPrerelease?: false;
    } & ReleasedProductProps)
);

const AddToCart = ({
  variantId,
  productName,
  grossPrice,
  netPrice,
  oldGrossPrice,
  minValue = 1,
  stock = 0,
  inShipping = false,
  unavailable = false,
  isPrerelease = false,
  withComplementary = false,
  currentSupplier,
  addToCart,
}: Props): JSX.Element => {
  const [itemQuantity, setItemQuantity] = useState(minValue);
  const [modalShown, setModalShown] = useState(false);
  const isAvailable = !inShipping && !unavailable;
  const availability = getMaxQuantity(stock);

  const enoughProduct = (!itemQuantity && stock) || itemQuantity <= stock;

  const { message } = [
    {
      condition: unavailable,
      message: messages.infoSuccessor,
    },
    {
      condition: inShipping,
      message: messages.askForAvailability,
    },
    {
      condition: isPrerelease,
      message: messages.askForProduct,
    },
    {
      condition: enoughProduct,
      message: messages.addToCart,
    },
  ].find((el) => el.condition) || {
    message: messages.askForAvailability,
  };

  const onButtonClick = () => {
    if (inShipping || itemQuantity > stock) {
      setModalShown(true);
    } else {
      addToCart?.(itemQuantity);
    }
  };

  const buttonProps: ButtonProps = {
    color: !isPrerelease && isAvailable && enoughProduct ? 'primary' : 'tertiary',
    icon: !isPrerelease && isAvailable && <Cart className={classes.buttonIcon} />,
    onClick: onButtonClick,
    wide: true,
    children: <FormattedMessage {...message} />,
  };

  return (
    <>
      <div
        style={createStyleVariables({
          gridTemplateColumns: isPrerelease ? 'auto auto' : `auto auto ${isAvailable ? '104px' : ''} 1fr`,
        })}
        className={classes.wrapper}
      >
        {isPrerelease ? (
          <div className={classes.prereleaseProduct}>
            <Typography variant="caption1" lineHeight="16px" className={classes.stayTuned}>
              <FormattedMessage {...messages.stayTuned} />
            </Typography>
            <Typography variant="h3" weight="700" renderAs="div">
              <FormattedMessage {...messages.prereleaseProduct} />
            </Typography>
          </div>
        ) : (
          <>
            <div className={classes.price}>
              {grossPrice && netPrice && (
                <ProductPrice
                  mobileAlign="right"
                  classes={{ gross: classes.gross }}
                  grossPrice={grossPrice}
                  netPrice={netPrice}
                  oldGrossPrice={oldGrossPrice}
                  withComplementary={withComplementary}
                />
              )}
            </div>
            <div className={classes.availability}>
              <ProductAvailability
                quantity={availability}
                unavailable={!isAvailable}
                currentSupplier={currentSupplier}
              />
            </div>
            {isAvailable && (
              <div className={classes.inputWrapper}>
                <InputQuantity
                  name="quantity"
                  value={itemQuantity}
                  minValue={minValue}
                  onBlur={() => setItemQuantity((value) => value || minValue)}
                  onChange={({ value }) => setItemQuantity(value)}
                  onIncrease={() => setItemQuantity((value) => (value || 0) + 1)}
                  onDecrease={() => setItemQuantity((value) => value - 1)}
                />
              </div>
            )}
          </>
        )}
        <div className={classes.buttonWrapper}>
          <Button {...buttonProps} dataEid="add-to-cart-button" />
        </div>
      </div>
      <div className={classes.buttonMobile}>
        <Button {...buttonProps} size="lg" />
      </div>
      <Modal
        controlledIsOpen={modalShown}
        onOpenChange={(isOpenState) => {
          setModalShown(isOpenState);
        }}
        hideWhenClickOutside
        withMobileClose
      >
        <AskForAvailability variantId={variantId} productName={productName} price={grossPrice} />
      </Modal>
    </>
  );
};

export default memo(AddToCart);
